import { useListSensors, type SensorsSensor, SensorsHealthStatus } from '@smarthome/api';
import {
  formatRelativeTime,
  ROUTES,
  useLocalStorage,
  useSensorStream,
  type SensorStreamEvent,
} from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, AlertCircle, Activity, Star, Pencil } from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { SearchField, Select, type SelectOption } from '@/components/ui';

type FilterMode = 'custom' | 'favorites' | 'dynamic';

// Dynamic sensors are created from device or scene state.
function isDynamicSensor(sensor: SensorsSensor): boolean {
  return (
    sensor.humanId?.startsWith('device:') ||
    sensor.humanId?.startsWith('scene:') ||
    sensor.provider === 'device' ||
    sensor.provider === 'scene'
  );
}

type SortOption = 'name' | 'value' | 'lastSeen';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'value', label: 'Value' },
  { value: 'lastSeen', label: 'Last updated' },
];

const INTEGER_FORMATTER = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const ONE_DECIMAL_FORMATTER = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatValue(value?: number): string {
  if (value === undefined || value === null) return '—';

  if (Number.isInteger(value)) {
    return INTEGER_FORMATTER.format(value);
  }
  return ONE_DECIMAL_FORMATTER.format(value);
}

function formatUnit(unit?: string): string {
  return unit || '';
}

function getHealthStatusColor(status?: string): string {
  switch (status) {
    case SensorsHealthStatus.HealthStatusLive:
      return 'bg-emerald-500 shadow-sm shadow-emerald-500/50';
    case SensorsHealthStatus.HealthStatusStale:
      return 'bg-yellow-500 shadow-sm shadow-yellow-500/50';
    case SensorsHealthStatus.HealthStatusMissing:
      return 'bg-red-500 shadow-sm shadow-red-500/50';
    default:
      return 'bg-gray-300 dark:bg-zinc-600';
  }
}

interface SensorRowProps {
  sensor: SensorsSensor;
}

function SensorRow({ sensor }: SensorRowProps) {
  const lastSeenText = sensor.lastSeen ? formatRelativeTime(sensor.lastSeen) : null;

  // Use humanId for URL if available, fall back to id
  const sensorSlug = sensor.humanId || sensor.id || '';

  return (
    <Link
      to={ROUTES.SENSOR_DETAIL.replace(':id', sensorSlug)}
      className="group flex items-center gap-4 border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-white/[0.04] dark:hover:bg-white/[0.02]"
    >
      {/* Status indicator */}
      <div className="flex-shrink-0">
        <div className={`h-2 w-2 rounded-full ${getHealthStatusColor(sensor.healthStatus)}`} />
      </div>

      {/* Name */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
          {sensor.name || sensor.humanId || 'Unnamed Sensor'}
        </p>
        {lastSeenText && (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-500">{lastSeenText}</p>
        )}
      </div>

      {/* Value */}
      <div className="flex-shrink-0 text-right">
        <span
          className={`text-lg font-semibold tabular-nums ${
            sensor.unitColor ? '' : 'text-gray-900 dark:text-white'
          }`}
          style={sensor.unitColor ? { color: sensor.unitColor } : undefined}
        >
          {formatValue(sensor.value)}
        </span>
        {sensor.unitSymbol && (
          <span className="ml-1 text-sm text-gray-500 dark:text-zinc-500">
            {formatUnit(sensor.unitSymbol)}
          </span>
        )}
      </div>
    </Link>
  );
}

interface VirtualizedSensorListProps {
  sensors: SensorsSensor[];
}

function VirtualizedSensorList({ sensors }: VirtualizedSensorListProps) {
  return (
    <div>
      {sensors.map((sensor) => (
        <SensorRow key={sensor.id} sensor={sensor} />
      ))}
    </div>
  );
}

export function SensorsPage() {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [filterMode, setFilterMode] = useLocalStorage<FilterMode>('sensors-filter-mode', 'custom');
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch, isFetching } = useListSensors({
    query: {
      refetchInterval: 30000, // Refresh every 30 seconds
      refetchOnWindowFocus: true,
    },
  });

  // Handle real-time sensor events
  const handleSensorEvent = useCallback(
    (event: SensorStreamEvent) => {
      if (event.type === 'value_changed' || event.type === 'config_changed') {
        // Update the sensor in the cache directly for instant UI update
        queryClient.setQueryData<{ sensors: SensorsSensor[] }>(['/api/sensors'], (oldData) => {
          if (!oldData?.sensors) return oldData;
          return {
            ...oldData,
            sensors: oldData.sensors.map((sensor) => {
              if (sensor.humanId === event.humanId || sensor.id === event.sensorId) {
                return {
                  ...sensor,
                  value: event.value ?? sensor.value,
                  lastSeen: event.timestamp,
                  name: event.name || sensor.name,
                };
              }
              return sensor;
            }),
          };
        });
      } else if (event.type === 'registered' || event.type === 'unregistered') {
        // For registration changes, refetch the full list
        queryClient.invalidateQueries({ queryKey: ['/api/sensors'] });
      }
    },
    [queryClient]
  );

  const { isConnected } = useSensorStream({
    enabled: true,
    onEvent: handleSensorEvent,
  });

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  // Build select options for sort
  const sortOptions: SelectOption[] = useMemo(() => {
    return SORT_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  const sensors = useMemo(() => data?.sensors || [], [data?.sensors]);

  // Filter and sort sensors
  const filteredSensors = useMemo(() => {
    let result = [...sensors];

    if (filterMode === 'favorites') {
      result = result.filter((sensor) => sensor.isFavorite && !isDynamicSensor(sensor));
    } else if (filterMode === 'dynamic') {
      result = result.filter((sensor) => isDynamicSensor(sensor));
    } else {
      result = result.filter((sensor) => !isDynamicSensor(sensor));
    }

    // Filter by search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (sensor) =>
          sensor.name?.toLowerCase().includes(searchLower) ||
          sensor.humanId?.toLowerCase().includes(searchLower) ||
          sensor.unitSymbol?.toLowerCase().includes(searchLower) ||
          sensor.unitLabel?.toLowerCase().includes(searchLower)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'value':
          return (b.value ?? -Infinity) - (a.value ?? -Infinity);
        case 'lastSeen':
          return new Date(b.lastSeen || 0).getTime() - new Date(a.lastSeen || 0).getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [sensors, search, sortBy, filterMode]);

  // Count favorites
  const favoritesCount = useMemo(() => {
    return sensors.filter((s) => s.isFavorite).length;
  }, [sensors]);

  // Count sensors with live health status
  const activeSensorCount = useMemo(() => {
    return sensors.filter((s) => s.healthStatus === SensorsHealthStatus.HealthStatusLive).length;
  }, [sensors]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Sensors
            </h1>
            <span
              className={`h-2 w-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-red-500'}`}
              title={isConnected ? 'Live updates connected' : 'Live updates disconnected'}
            />
          </div>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            {isLoading
              ? 'Loading...'
              : `${sensors.length} sensor${sensors.length !== 1 ? 's' : ''} · ${activeSensorCount} active`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={ROUTES.SENSORS_BULK_EDIT}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white dark:hover:border-white/[0.1] dark:hover:bg-[#1f1f23]"
          >
            <Pencil className="h-4 w-4" />
            Bulk edit
          </Link>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white dark:hover:border-white/[0.1] dark:hover:bg-[#1f1f23]"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          {/* Filter */}
          <div
            className={`flex rounded-lg border border-gray-200 bg-white p-0.5
              dark:border-white/[0.06] dark:bg-[#18181b]`}
          >
            <button
              onClick={() => setFilterMode('custom')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium
                transition-colors ${
                  filterMode === 'custom'
                    ? 'bg-gray-100 text-gray-900 dark:bg-white/[0.08] dark:text-white'
                    : [
                        'text-gray-600 hover:bg-gray-100',
                        'dark:text-zinc-400 dark:hover:bg-white/[0.04]',
                      ].join(' ')
                }`}
            >
              Defined
            </button>
            <button
              onClick={() => setFilterMode('favorites')}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium
                transition-colors ${
                  filterMode === 'favorites'
                    ? 'bg-gray-100 text-gray-900 dark:bg-white/[0.08] dark:text-white'
                    : [
                        'text-gray-600 hover:bg-gray-100',
                        'dark:text-zinc-400 dark:hover:bg-white/[0.04]',
                      ].join(' ')
                }`}
            >
              <Star
                className="h-3.5 w-3.5"
                fill={filterMode === 'favorites' ? 'currentColor' : 'none'}
              />
              Favorites
              {favoritesCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-zinc-500">({favoritesCount})</span>
              )}
            </button>
            <button
              onClick={() => setFilterMode('dynamic')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filterMode === 'dynamic'
                  ? 'bg-gray-100 text-gray-900 dark:bg-white/[0.08] dark:text-white'
                  : [
                      'text-gray-600 hover:bg-gray-100',
                      'dark:text-zinc-400 dark:hover:bg-white/[0.04]',
                    ].join(' ')
              }`}
            >
              Dynamic
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-full sm:w-64">
            <SearchField
              label="Search"
              hideLabel
              placeholder="Search sensors..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <Select
            label="Sort"
            hideLabel
            value={sortBy}
            onChange={(v) => setSortBy(v as SortOption)}
            options={sortOptions}
            className="w-full sm:w-40"
          />
        </div>
      </div>

      {/* Sensor list */}
      <div className="min-h-48 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
          </div>
        ) : isError ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">Failed to load sensors</p>
            <button
              onClick={() => refetch()}
              className="mt-3 text-sm text-blue-500 hover:text-blue-400"
            >
              Try again
            </button>
          </div>
        ) : filteredSensors.length === 0 ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
              {filterMode === 'favorites' ? (
                <Star className="h-6 w-6 text-gray-500 dark:text-zinc-500" fill="none" />
              ) : (
                <Activity className="h-6 w-6 text-gray-500 dark:text-zinc-500" />
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-zinc-400">
              {search
                ? 'No sensors match your search'
                : filterMode === 'favorites'
                  ? 'No favorite sensors yet'
                  : filterMode === 'dynamic'
                    ? 'No dynamic sensors yet'
                    : 'No defined sensors found'}
            </p>
            {search ? (
              <button
                onClick={() => setSearch('')}
                className="mt-3 text-sm text-blue-500 hover:text-blue-400"
              >
                Clear search
              </button>
            ) : filterMode === 'favorites' || filterMode === 'dynamic' ? (
              <button
                onClick={() => setFilterMode('custom')}
                className="mt-3 text-sm text-blue-500 hover:text-blue-400"
              >
                View defined sensors
              </button>
            ) : null}
          </div>
        ) : (
          <VirtualizedSensorList sensors={filteredSensors} />
        )}
      </div>
    </div>
  );
}
